// RCS URL helpers extracted from legacy mixin behavior.
// These validators support static RFC 3986 URLs and dynamic URLs with placeholders.

const DYNAMIC_PLACEHOLDER_PATTERN = /(<[a-zA-Z0-9_]+>|\{\{\s*\d+\s*\}\})/;
const ANGLE_PLACEHOLDER_PATTERN = /<([a-zA-Z0-9_]+)>/;
const ANGLE_PLACEHOLDER_REGEX = /<([a-zA-Z0-9_]+)>/g;
const CURLY_PLACEHOLDER_REGEX = /\{\{\s*(\d+)\s*\}\}/g;

function hasInvalidWhitespace(url) {
  return /\s/.test(url);
}

function hasUnmatchedAngleBrackets(url) {
  const openCount = (url.match(/</g) || []).length;
  const closeCount = (url.match(/>/g) || []).length;
  return openCount !== closeCount;
}

function hasUnsupportedAnglePlaceholder(url) {
  return /<[^>]*>/.test(url) && !ANGLE_PLACEHOLDER_PATTERN.test(url);
}

function toValidatableUrl(rawUrl, isDynamic) {
  const trimmed = (rawUrl || "").trim();
  if (!trimmed) return "";

  if (!isDynamic) return trimmed;

  return trimmed
    .replace(ANGLE_PLACEHOLDER_REGEX, "dynamic")
    .replace(CURLY_PLACEHOLDER_REGEX, "dynamic");
}

// A placeholder-only value (e.g. "<landing_url>") has no static URL base to validate as a URL.
function isPlaceholderOnlyUrl(rawUrl) {
  const remainder = rawUrl
    .replace(ANGLE_PLACEHOLDER_REGEX, "")
    .replace(CURLY_PLACEHOLDER_REGEX, "");
  return remainder.trim() === "";
}

export function validateRcsUrl(url, isDynamic = false) {
  const raw = (url || "").trim();
  if (!raw) return false;
  if (hasInvalidWhitespace(raw)) return false;

  const containsPlaceholder = DYNAMIC_PLACEHOLDER_PATTERN.test(raw);

  if (!isDynamic && containsPlaceholder) return false;

  if (isDynamic) {
    if (hasUnmatchedAngleBrackets(raw)) return false;
    if (hasUnsupportedAnglePlaceholder(raw)) return false;
    if (containsPlaceholder && isPlaceholderOnlyUrl(raw)) return true;
  }

  const candidate = toValidatableUrl(raw, isDynamic);

  try {
    const parsed = new URL(candidate);
    if (!['http:', 'https:'].includes(parsed.protocol)) return false;
    return true;
  } catch (error) {
    return false;
  }
}

function normalizeUrlType(urlType, defaultUrlType) {
  const effective = (urlType || defaultUrlType || "static").toString().toLowerCase();
  return effective === "dynamic" ? "dynamic" : "static";
}

function shouldValidateButtonUrl(button) {
  if (!button || typeof button !== "object") return false;

  const type = (button.type || button.postback_action_type || "").toString().toLowerCase();
  const urlLikeTypes = new Set(["website", "webview", "app_webview", "url", "openurl"]);

  return Boolean(button.url) || urlLikeTypes.has(type);
}

function extractButtonUrl(button) {
  if (!button || typeof button !== "object") return "";

  if (typeof button.url === "string" && button.url.trim()) {
    return button.url.trim();
  }

  // Some payloads store URL in payload for URL-like button actions.
  if (typeof button.payload === "string" && button.payload.trim()) {
    return button.payload.trim();
  }

  return "";
}

function collectButtons(message) {
  const buttons = [];
  const template = message?.template;

  if (Array.isArray(template?.buttons)) {
    buttons.push(...template.buttons);
  }

  if (Array.isArray(template?.elements)) {
    for (const element of template.elements) {
      if (Array.isArray(element?.buttons)) {
        buttons.push(...element.buttons);
      }
    }
  }

  return buttons;
}

export function findInvalidRcsButtonUrl(messages, defaultUrlType = "static") {
  if (!Array.isArray(messages)) return null;

  for (const message of messages) {
    const buttons = collectButtons(message);

    for (const button of buttons) {
      if (!shouldValidateButtonUrl(button)) continue;

      const url = extractButtonUrl(button);
      if (!url) continue;

      const effectiveUrlType = normalizeUrlType(button.url_type, defaultUrlType);
      const isDynamic = effectiveUrlType === "dynamic";

      if (!validateRcsUrl(url, isDynamic)) {
        return url;
      }
    }
  }

  return null;
}
